import { addDays, startOfWeek, getISOWeek } from 'date-fns';
import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import { toIsoDay, formatDayMonth, parseIsoDay } from '../overviewDates';
import { buildPeopleRows, countByStatus } from './panoramaPeopleRows';
import type { PeriodRange } from './panoramaPeriod';

const DEFAULT_WEEKS_AHEAD = 12;

export interface WeekCapacity {
	weekStart: string;
	weekEnd: string;
	weekLabel: string;
	weekRangeLabel?: string;
	onVacation: number;
	scheduled: number;
	pending: number;
	isAboveLimit: boolean;
	wouldExceedWithPending: boolean;
}

export interface CapacitySnapshot {
	weeks: WeekCapacity[];
	capacityLimitPercent: number;
	limitCount: number;
	baseCount: number;
	weeksAboveLimit: number;
}

export function buildCapacitySnapshot(
	documents: VacationOverviewDocument[],
	referenceDate: Date,
	capacityLimitPercent: number = 15,
	periodOrWeeks: PeriodRange | number = DEFAULT_WEEKS_AHEAD
): CapacitySnapshot {
	const activeBase = documents.filter(d => d.active && d.inBase);
	const baseCount = activeBase.length;
	const limitCount = Math.ceil((baseCount * capacityLimitPercent) / 100);

	const weeks: WeekCapacity[] = [];
	const todayIso = toIsoDay(referenceDate);

	let firstWeek: Date;
	let lastWeekDate: Date;

	if (typeof periodOrWeeks === 'object') {
		const periodStart = parseIsoDay(periodOrWeeks.from);
		const periodEnd = parseIsoDay(periodOrWeeks.to);
		if (!periodStart || !periodEnd) {
			return { weeks: [], capacityLimitPercent, limitCount, baseCount, weeksAboveLimit: 0 };
		}
		firstWeek = startOfWeek(periodStart, { weekStartsOn: 1 });
		lastWeekDate = periodEnd;
	} else {
		firstWeek = startOfWeek(referenceDate, { weekStartsOn: 1 });
		lastWeekDate = addDays(firstWeek, (periodOrWeeks - 1) * 7);
	}

	for (let weekStart = firstWeek; weekStart <= lastWeekDate; weekStart = addDays(weekStart, 7)) {
		const weekStartIso = toIsoDay(weekStart);
		const weekEndIso = toIsoDay(addDays(weekStart, 6));
		const isoWeek = getISOWeek(weekStart);

		const weekRows = buildPeopleRows(documents, referenceDate, { from: weekStartIso, to: weekEndIso });
		const counts = countByStatus(weekRows);

		const onVacation = counts['on-vacation'] ?? 0;
		const scheduled = counts.scheduled ?? 0;
		const pending = (counts.pending ?? 0) + (counts['approval-urgent'] ?? 0);
		const isAboveLimit = (onVacation + scheduled) > limitCount;
		const wouldExceedWithPending = (onVacation + scheduled + pending) > limitCount;

		weeks.push({
			weekStart: weekStartIso,
			weekEnd: weekEndIso,
			weekLabel: `Semana ${isoWeek}`,
			weekRangeLabel: `${formatDayMonth(weekStartIso)} a ${formatDayMonth(weekEndIso)}`,
			onVacation,
			scheduled,
			pending,
			isAboveLimit,
			wouldExceedWithPending,
		});
	}

	return {
		weeks,
		capacityLimitPercent,
		limitCount,
		baseCount,
		weeksAboveLimit: weeks.filter(w => w.isAboveLimit).length,
	};
}
